import { toast } from 'react-toastify';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit/store';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import type { ApplyEndorsementPayload } from '@domain/schemas/payload.schema';
import { canChange } from '@helpers/canChange';
import { DocumentMode } from '@helpers/document-mode';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { patchEntity } from '@mutations/patchEntity';
import { entityQueries, fetchEntity } from '@queries/fetchEntity';
import { invalidateSavedViewCounts } from '@queries/fetchSavedViews';
import { reorderLayout } from '@root/helpers';
import { getResourcesListToFetch } from '@root/src/utils/get-resources-list-to-fetch';
import type { UpdateResourceDataApiPayload } from '@src/mutations/payloads';
import { isEndorsementViewPath, isFormViewPath } from '@utils/app-paths';
import { queryClient } from '@utils/queryClient';
import { isEqual } from 'lodash-es';
import { createWithEqualityFn } from 'zustand/traditional';

import { createAttachmentsSlice } from './attachments.slice.store';
import { useDrawerStore } from './drawer.store';
import { createFormCollapsedSlice } from './form-collapsed.slice.store';
import { createHighlightSlice } from './highlight.slice.store';
import { createLibrarySlice } from './library.slice.store';
import { mergeAuthoritativeFields } from './merge-authoritative-fields';
import { createModalsSlice } from './modals.slice.store';
import { useMultiSelectStore } from './multi-select.store';
import type { MainStore } from './types';
import { fetchSchemaIfNotAvailableInStore, updateFormValuesInForeignStore, updateSchemaInForeignStore } from './utils';

export const useEntityStore = createWithEqualityFn<MainStore>(
  (set, get, ...rest) => ({
    ...createFormCollapsedSlice(set, get, ...rest),
    ...createHighlightSlice(set, get, ...rest),
    ...createModalsSlice(set, get, ...rest),
    ...createLibrarySlice(set, get, ...rest),
    ...createAttachmentsSlice(set, get, ...rest),
    isLoading: false,
    resetState: () => {
      set({
        attachments: [],
        submission: undefined,
        endorsementParent: undefined,
        settings: { open: false },
        modal: { open: false },
      });
      useMultiSelectStore.getState().clearSelection();
    },
    reloadSubmission: async () => {
      set({ isLoading: true });

      const { ids, mode } = getDocumentMode(location.pathname);

      const [id1, id2] = ids;
      const cacheEntityId = mode === DocumentMode.ENDORSEMENTS && id2 ? id2 : id1;

      await fetchSchemaIfNotAvailableInStore();
      const submission = await queryClient.fetchQuery({ ...entityQueries.detail(cacheEntityId), staleTime: 0 });

      // Skip the state swap + re-render when the fetched data is identical to what's
      // already loaded — the common case when returning to a tab. Each set() below
      // forces a synchronous re-render of the entire contract editor (seconds on large
      // contracts via useSyncExternalStore); a deep compare is single-digit ms.
      const current = get().submission;
      if (current !== undefined && isEqual(submission, current)) {
        set({ isLoading: false });
        return submission;
      }

      const parentId = submission?.parentId;

      if (parentId) {
        const response = await fetchEntity({
          dataRange: getResourcesListToFetch(),
          documentMode: DocumentMode.SUBMISSIONS,
        });
        set({ submission, endorsementParent: response });
      } else {
        set({ submission });
      }

      set({ loadingBlockId: undefined });

      get().reloadAttachments();
      updateSchemaInForeignStore(submission.data_items);
      updateFormValuesInForeignStore(submission.data_items);

      const { activePartId, selectedBlockIds, selectAllInPart } = useMultiSelectStore.getState();
      if (!isFormViewPath() && canChange(submission?.status) && activePartId && selectedBlockIds.size > 0) {
        const existingIds = new Set(submission?.sections?.flatMap((s) => s.blocks.map((b) => b.id)) ?? []);
        const pruned = [...selectedBlockIds].filter((id) => existingIds.has(id));
        if (pruned.length < selectedBlockIds.size) {
          selectAllInPart(activePartId, pruned);
        }
      }

      set({ isLoading: false });
      return submission;
    },
    createBlock: async (block, prevBlockOrder) => {
      const {
        blockLibraryId,
        clause_reference,
        clause_type,
        content,
        linkedDatapoints = [],
        name,
        repeaterIds,
        section_id,
        shouldInsertPageBreak,
        title,
        type,
        variations,
        canDelete = true,
        canEdit = true,
      } = block;
      const submission = get().submission;

      if (!submission) {
        return;
      }

      set({ isLoading: true });
      try {
        const result = await patchEntity({
          operation: ApiPatchOperation.CreateResourceBlock,
          blockLibraryId,
          canDelete,
          canEdit,
          clause_reference,
          clause_type,
          content,
          linkedDatapoints,
          name,
          prevBlockOrder,
          repeaterIds,
          section_id,
          shouldInsertPageBreak,
          title,
          type,
          variations,
        });

        if (result?.message === 'success') {
          await get().reloadSubmission();
          get().closeModal();
          get().highlightChangedBlock(result.blockId);
        }
      } finally {
        set({ isLoading: false });
      }
    },

    updateBlock: async (block) => {
      const { id: blockId, ...blockPayload } = block;

      set({ loadingBlockId: blockId });
      const submission = get().submission;
      if (!submission) {
        return;
      }

      const newSection = submission.sections.find((section) => section.id === block.section_id);

      if (!newSection) {
        throw new Error(`Section "${block.section_id}" does not exist`);
      }

      set({ isLoading: true });

      try {
        const result = await patchEntity({
          operation: ApiPatchOperation.UpdateResourceBlock,
          blockId,
          block: blockPayload,
        });

        if (result?.message === 'success') {
          await get().reloadSubmission();
          get().closeModal();
          get().highlightChangedBlock(blockId);
        }
      } finally {
        set({ loadingBlockId: undefined, isLoading: false });
      }
    },
    updateFormData: async (data_items: any) => {
      const submission = get().submission;

      if (!submission) {
        return;
      }

      set({ isLoading: true });
      try {
        const result = await patchEntity({
          operation: ApiPatchOperation.UpdateResourceDataItems,
          data_items,
        });

        if (result?.message === 'success') {
          await get().reloadSubmission();
        }

        return result;
      } finally {
        set({ isLoading: false });
      }
    },
    updateResourceData: async (data, id, shouldBypassDatapointValidation) => {
      const submission = get().submission;
      const submissionId = id ?? submission?.id;

      if (!submissionId) {
        return;
      }

      const { mode } = getDocumentMode(location.pathname);
      // Finalising an endorsement is its own operation. Its handler reads only `operation`; `status` stays as the
      // marker that request logs and the e2e helpers key on. Form fields and the bypass flag are not sent.
      const isApplyEndorsement = mode === DocumentMode.ENDORSEMENTS && data?.status === 'FINAL';
      const payload: ApplyEndorsementPayload | UpdateResourceDataApiPayload = isApplyEndorsement
        ? { operation: ApiPatchOperation.ApplyEndorsement, status: 'FINAL' }
        : // `data.status` is the SPA's string-literal status, while the route schemas type status as the ResourceState
          // enum. The route validator for the open resource enforces the exact shape.
          ({
            operation: ApiPatchOperation.UpdateResourceData,
            ...data,
            shouldBypassDatapointValidation,
          } as UpdateResourceDataApiPayload);

      set({ isLoading: true });
      try {
        const result = await patchEntity(payload, id);

        // Any accepted PATCH carrying a status can move this contract in or out of saved views.
        if (result?.message === 'success' && data.status !== undefined) {
          invalidateSavedViewCounts(queryClient);
        }

        if (submission) {
          await get().reloadSubmission();
          // patchEntity rejects on any non-2xx (axios interceptor), so a resolved result means the server took the
          // fields; only then overlay them, or a rejected status/name would land in the store.
          const reloaded = get().submission;
          if (reloaded && result) {
            const { merged, diverged } = mergeAuthoritativeFields(reloaded, {
              status: data.status,
              name: data.name,
            });
            if (diverged) {
              set({ submission: merged });
            }
          }
        }

        return result;
      } finally {
        set({ isLoading: false });
      }
    },
    deleteBlock: async (blockId, shouldReloadSubmission = true) => {
      set({ loadingBlockId: blockId });
      const submission = get().submission;

      if (!submission) {
        return;
      }

      set({ isLoading: true });

      try {
        const result = await patchEntity({
          operation: ApiPatchOperation.DeleteResourceBlock,
          blockId,
        });

        if (result?.message === 'success' && shouldReloadSubmission) {
          await get().reloadSubmission();
          toast.success('Removed block');
        }
      } finally {
        set({ isLoading: false });
      }
    },
    bulkDeleteBlocks: async (partId, blockIds) => {
      const submission = get().submission;
      const submissionId = submission?.id;

      if (!submission || blockIds.length === 0) {
        return false;
      }

      set({ isLoading: true });
      let success = false;

      try {
        const operation = isEndorsementViewPath()
          ? ApiPatchOperation.BulkDeleteSummaryBlocks
          : ApiPatchOperation.BulkDeleteResourceBlocks;
        const result = await patchEntity({
          operation,
          sectionId: partId,
          blockIds,
        });

        success = result?.message === 'success';

        if (success) {
          useMultiSelectStore.getState().clearSelection();
          const editingId = useBlockEditFormStore.getState().formValues?.id;
          const isStillOnSameSubmission = get().submission?.id === submissionId;
          if (editingId && blockIds.includes(editingId) && isStillOnSameSubmission) {
            useBlockEditFormStore.getState().reset();
            const drawer = useDrawerStore.getState();
            if (drawer.state?.type === DrawerLayoutComponent.BlockConfigurationPanel) {
              drawer.closeDrawer();
            }
          }
        } else {
          toast.error('Failed to remove blocks. Please try again.');
        }
      } catch {
        toast.error('Failed to remove blocks. Please try again.');
      } finally {
        // Sync submission state on every path — success renders the deletion, failure prunes
        // stale ids (e.g. deleted by another tab). Selection is preserved on failure so the
        // multi-select panel stays in sync with the still-open confirm modal. Best-effort
        // reload: swallow secondary failures so they don't escape as unhandled rejections.
        try {
          await get().reloadSubmission();
        } catch {
          // intentional: nothing useful to do after a failed reload here
        }
        set({ isLoading: false });
      }

      return success;
    },

    reorderBlock: async (blockId, destinationIndex, destinationSectionId) => {
      const submission = get().submission;
      const blockSection = submission?.sections.find((section) => section.blocks.some((item) => item.id === blockId));
      const blockBeingMoved = blockSection?.blocks.find((block) => block.id === blockId);

      if (!submission || !blockSection || !blockBeingMoved) {
        return;
      }

      const isMovingBetweenSections = !!destinationSectionId && destinationSectionId !== blockSection.id;

      const updatedSectionLayout = reorderLayout(
        blockSection.layout,
        blockId,
        isMovingBetweenSections ? -1 : destinationIndex,
      );

      // Optimistically update UI
      set({
        submission: {
          ...submission,
          sections: submission.sections.map((item) => {
            if (item.id === blockSection.id) {
              return {
                ...item,
                layout: updatedSectionLayout,
                blocks: isMovingBetweenSections ? item.blocks.filter((item) => item.id !== blockId) : item.blocks,
              };
            }

            if (item.id === destinationSectionId) {
              return {
                ...item,
                layout: reorderLayout(item.layout, blockId, destinationIndex),
                blocks: [...item.blocks, blockBeingMoved],
              };
            }

            return item;
          }),
        },
      });

      set({ isLoading: true });
      try {
        await patchEntity({
          blockId,
          reorder: { destinationIndex, destinationSectionId },
          operation: ApiPatchOperation.ReorderResourceBlock,
        });

        await get().reloadSubmission();
      } finally {
        set({ isLoading: false });
      }
    },
    changeDocumentBase: async (sourceResourceId) => {
      const doc = get().submission;

      if (!doc) {
        return;
      }

      set({ isLoading: true });
      try {
        const result = await patchEntity({
          operation: ApiPatchOperation.ChangeBaseTemplate,
          sourceResourceId,
        });

        if (result?.message === 'success') {
          await get().reloadSubmission();
        }

        return result;
      } finally {
        set({ isLoading: false });
      }
    },
  }),
  isEqual,
);
